// See https://www.raymondcamden.com/2019/10/16/using-indexeddb-with-vuejs
// also  https://medium.com/@mntlmaxi/make-your-vue-app-last-with-indexeddb-66f02708830e
// Async/await ref -= https://javascript.info/async-await

const DB_NAME = 'VesselActivityCaptureTool';
const DB_VERSION = 7;

const VOYAGE_REPORTS_OBJ_STORE = 'voyageReports';
const VESSEL_ACTIVITY_OBJ_STORE = 'vesselActivities';
const BULK_TRANSACTION_OBJ_STORE = 'bulkTransactions';
const TANK_STATUS_OBJ_STORE = 'tankStatuses';
const PASSENGER_OBJ_STORE = 'passengers';
const SAFETY_OBSERVATION_OBJ_STORE = 'safetyObservations';
const CARGO_LINE_OBJ_STORE = 'cargoLines';

let DB;

export default {

  async getDb() {
    return new Promise((resolve, reject) => {
      if (DB) { return resolve(DB); }

      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (e) => {
        console.error('Error opening db', e);
        reject(new Error('Error'));
      };

      request.onsuccess = (e) => {
        DB = e.target.result;
        console.log(`Opening IndexedDb Name: '${DB.name}', Version: ${DB.version}`);
        resolve(DB);
      };

      request.onupgradeneeded = (e) => {
        console.warn('Indexed Db - onupgradeneeded called');

        const db = e.target.result;

        [
          VOYAGE_REPORTS_OBJ_STORE,
          VESSEL_ACTIVITY_OBJ_STORE,
          BULK_TRANSACTION_OBJ_STORE,
          TANK_STATUS_OBJ_STORE,
          PASSENGER_OBJ_STORE,
          SAFETY_OBSERVATION_OBJ_STORE,
          CARGO_LINE_OBJ_STORE]
          .forEach(storeName => {
            if (!db.objectStoreNames.contains(storeName)) {
              db.createObjectStore(storeName, { keyPath: 'idc' });
              console.log('Updated Object Stores with, ', storeName);
            }
          });
      };

      return true;
    });
  },

  async getAll(storeName) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readonly');
      const values = [];
      trans.oncomplete = () => {
        resolve(values);
      };

      const store = trans.objectStore(storeName);

      store.openCursor().onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          values.push(cursor.value);
          cursor.continue();
        }
      };
    });
  },

  async get(storeName, idc) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readonly');
      let value = null;
      trans.oncomplete = () => {
        resolve(value);
      };

      const store = trans.objectStore(storeName);
      const getRequest = store.get(idc);

      getRequest.onsuccess = () => {
        const { result } = getRequest;
        value = result;
      };
    });
  },

  async put(storeName, value) {
    let result = null;
    const db = await this.getDb();
    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readwrite');
      trans.oncomplete = () => {
        resolve(result);
      };

      const idbValue = { ...value };
      if (!idbValue.idc) {
        const idc = new Date().getTime();
        idbValue.idc = idc;
      }

      const store = trans.objectStore(storeName);
      const putRequest = store.put(idbValue);

      putRequest.onsuccess = () => {
        const resultIdc = putRequest.result;
        result = resultIdc;
      };
    });
  },

  async putMany(storeName, values) {
    const delayPromise = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    for (const value of values) {
      await this.put(storeName, value);
      await delayPromise(50);
    }
  },

  async delete(storeName, value) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      const store = trans.objectStore(storeName);
      store.delete(value.idc);
    });
  },

  async deleteMany(storeName, values) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      const store = trans.objectStore(storeName);

      values.forEach((x) => {
        store.delete(x.idc);
      });
    });
  },

  async deleteAll(storeName) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([storeName], 'readwrite');
      trans.oncomplete = () => {
        resolve(true);
      };

      const store = trans.objectStore(storeName);
      store.clear();
    });
  },

  async getDataToBeSynced() {
    const promVoyageReportsToBeSynced = this.getVoyageReports();
    const promVesselActivitiesToBeSynced = this.getVesselActivities();
    const promBulkTransactionsToBeSynced = this.getBulkTransactions();
    const promTanksWithCurrentStatusToBeSynced = this.getTanksWithCurrentStatus();
    const promPassengersToBeSynced = this.getPassengers();
    const promSafetyObservationsToBeSynced = this.getSafetyObservations();
    const promCargoLinesToBeSynced = this.getCargoLines();

    const results = await Promise.all([
      promVoyageReportsToBeSynced,
      promVesselActivitiesToBeSynced,
      promBulkTransactionsToBeSynced,
      promTanksWithCurrentStatusToBeSynced,
      promPassengersToBeSynced,
      promSafetyObservationsToBeSynced,
      promCargoLinesToBeSynced
    ]);

    const voyageReportsToBeSynced = results[0];
    const vesselActivitiesToBeSynced = results[1];
    const bulkTransactionsToBeSynced = results[2];
    const tanksWithCurrentStatusToBeSynced = results[3];
    const passengersToBeSynced = results[4];
    const safetyObservationsToBeSynced = results[5];
    const allCargoLinesToBeSynced = results[6];

    const dataToBeSynced = {
      voyageReportsToBeSynced,
      vesselActivitiesToBeSynced,
      bulkTransactionsToBeSynced,
      tanksWithCurrentStatusToBeSynced,
      passengersToBeSynced,
      safetyObservationsToBeSynced,
      allCargoLinesToBeSynced,
      // Add other offline data to be synced here.
    };

    return new Promise((resolve) => {
      resolve(dataToBeSynced);
    });
  },

  // Voyage Reports
  async deleteVoyageReport(voyageReport) {
    return this.delete(VOYAGE_REPORTS_OBJ_STORE, voyageReport);
  },

  async deleteMultipleVoyageReports(voyageReports) {
    return this.deleteMany(VOYAGE_REPORTS_OBJ_STORE, voyageReports);
  },

  async deleteAllVoyageReports() {
    return this.deleteAll(VOYAGE_REPORTS_OBJ_STORE);
  },

  async getVoyageReports() {
    return this.getAll(VOYAGE_REPORTS_OBJ_STORE);
  },

  async saveVoyageReport(voyageReport) {
    return this.put(VOYAGE_REPORTS_OBJ_STORE, voyageReport);
  },

  async updateVoyageReport(updateData) {
    return this.put(VOYAGE_REPORTS_OBJ_STORE, updateData);
  },


  // **** Vessel Activity Local Storage ****

  async getVesselActivities() {
    return this.getAll(VESSEL_ACTIVITY_OBJ_STORE);
  },

  async getVesselActivityByIdc(idc) {
    return this.get(VESSEL_ACTIVITY_OBJ_STORE, idc);
  },

  async saveVesselActivity(vesselActivity) {
    return this.put(VESSEL_ACTIVITY_OBJ_STORE, vesselActivity);
  },

  async updateVesselActivity(updateData) {
    return this.put(VESSEL_ACTIVITY_OBJ_STORE, updateData);
  },

  async updateMultipleVesselActivities(updateData) {
    return this.putMany(VESSEL_ACTIVITY_OBJ_STORE, updateData);
  },

  async deleteVesselActivity(vesselActivity) {
    return this.delete(VESSEL_ACTIVITY_OBJ_STORE, vesselActivity);
  },

  async deleteMultipleVesselActivities(activities) {
    return this.deleteMany(VESSEL_ACTIVITY_OBJ_STORE, activities);
  },

  async deleteAllVesselActivities() {
    return this.deleteAll(VESSEL_ACTIVITY_OBJ_STORE);
  },

  // **** Bulk Transactions Local Storage ****
  async getBulkTransactions() {
    return this.getAll(BULK_TRANSACTION_OBJ_STORE);
  },

  async getBulkTransactionByIdc(idc) {
    return this.get(BULK_TRANSACTION_OBJ_STORE, idc);
  },

  async getBulkTransactionFromActivityStore(vesselActivityIdc, bulkTransactionIdc) {
    const db = await this.getDb();

    return new Promise((resolve) => {
      const trans = db.transaction([VESSEL_ACTIVITY_OBJ_STORE], 'readonly');
      let bulkTransaction = null;
      trans.oncomplete = () => {
        resolve(bulkTransaction);
      };

      const store = trans.objectStore(VESSEL_ACTIVITY_OBJ_STORE);
      const getRequest = store.get(vesselActivityIdc);

      getRequest.onsuccess = () => {
        const { result } = getRequest;
        bulkTransaction = result.bulkTransactions.find((x) => x.idc === bulkTransactionIdc);
      };
    });
  },

  async saveBulkTransaction(bulkTransaction) {
    return this.put(BULK_TRANSACTION_OBJ_STORE, bulkTransaction);
  },

  async updateBulkTransaction(updateData) {
    return this.put(BULK_TRANSACTION_OBJ_STORE, updateData);
  },

  async deleteBulkTransaction(idc) {
    return this.delete(BULK_TRANSACTION_OBJ_STORE, idc);
  },

  async deleteMultipleBulkTransactions(transactions) {
    return this.deleteMany(BULK_TRANSACTION_OBJ_STORE, transactions);
  },

  async deleteAllBulkTransactions() {
    return this.deleteAll(BULK_TRANSACTION_OBJ_STORE);
  },

  // **** Tank Status Local Storage ****
  async getTanksWithCurrentStatus() {
    return this.getAll(TANK_STATUS_OBJ_STORE);
  },

  async saveTankStatus(tankStatus) {
    return this.put(TANK_STATUS_OBJ_STORE, tankStatus);
  },

  async updateTankStatus(updateData) {
    return this.put(TANK_STATUS_OBJ_STORE, updateData);
  },

  async deleteTankStatus(idc) {
    return this.delete(TANK_STATUS_OBJ_STORE, idc);
  },

  async deleteMultipleTankStatuses(statuses) {
    return this.deleteMany(TANK_STATUS_OBJ_STORE, statuses);
  },

  async deleteAllTankStatuses() {
    return this.deleteAll(TANK_STATUS_OBJ_STORE);
  },

  // **** Passengers Local Storage ****
  async getPassengers() {
    return this.getAll(PASSENGER_OBJ_STORE);
  },

  async getPassengerByIdc(idc) {
    return this.get(PASSENGER_OBJ_STORE, idc);
  },

  async savePassenger(passenger) {
    return this.put(PASSENGER_OBJ_STORE, passenger);
  },

  async updatePassenger(updateData) {
    return this.put(PASSENGER_OBJ_STORE, updateData);
  },

  async deletePassenger(passenger) {
    return this.delete(PASSENGER_OBJ_STORE, passenger);
  },

  async deleteMultiplePassengers(passengers) {
    return this.deleteMany(PASSENGER_OBJ_STORE, passengers);
  },

  async deleteAllPassengers() {
    return this.deleteAll(PASSENGER_OBJ_STORE);
  },

  // **** Safety Observations Local Storage ****
  async getSafetyObservations() {
    return this.getAll(SAFETY_OBSERVATION_OBJ_STORE);
  },

  async getSafetyObservationByIdc(idc) {
    return this.get(SAFETY_OBSERVATION_OBJ_STORE, idc);
  },

  async saveSafetyObservation(safetyObservation) {
    return this.put(SAFETY_OBSERVATION_OBJ_STORE, safetyObservation);
  },

  async updateSafetyObservation(updateData) {
    return this.put(SAFETY_OBSERVATION_OBJ_STORE, updateData);
  },

  async deleteSafetyObservation(safetyObservation) {
    return this.delete(SAFETY_OBSERVATION_OBJ_STORE, safetyObservation);
  },

  async deleteMultipleSafetyObservations(safetyObservations) {
    return this.deleteMany(SAFETY_OBSERVATION_OBJ_STORE, safetyObservations);
  },

  async deleteAllSafetyObservations() {
    return this.deleteAll(SAFETY_OBSERVATION_OBJ_STORE);
  },

  // **** Cargo Lines Local Storage ****
  async getCargoLines() {
    return this.getAll(CARGO_LINE_OBJ_STORE);
  },

  async getCargoLineByIdc(idc) {
    return this.get(CARGO_LINE_OBJ_STORE, idc);
  },

  async saveCargoLine(cargoLine) {
    return this.put(CARGO_LINE_OBJ_STORE, cargoLine);
  },

  async updateCargoLine(updateData) {
    return this.put(CARGO_LINE_OBJ_STORE, updateData);
  },

  async updateMultipleCargoLines(updateData) {
    return this.putMany(CARGO_LINE_OBJ_STORE, updateData);
  },

  async deleteCargoLine(cargoLine) {
    return this.delete(CARGO_LINE_OBJ_STORE, cargoLine);
  },

  async deleteMultipleCargoLines(cargoLines) {
    return this.deleteMany(CARGO_LINE_OBJ_STORE, cargoLines);
  },

  async deleteAllCargoLines() {
    return this.deleteAll(CARGO_LINE_OBJ_STORE);
  }
};
